import type {Component} from 'grapesjs';
import type {PrimaryConfigOptions} from '../index';

interface PresetComponentVO {
  id: number;
  render: (selectedPresetID: number) => string;
}

interface ColumnsSelectorProps {
  type: string;
  property: string;
  component?: Component;
  callback?: (...args: any) => void;
  list: PresetComponentVO[];
  previewIdFixedList: number[];
  initalSelectedId: number;
  numberOfFixedComponents: number;
  default?: string;
  name?: string;
  classPrefix?: string;
}

interface EmitData {
  labelContainer?: HTMLElement;
  columnsSelector?: HTMLElement;
  presetID?: number;
  toggleButton?: Event;
}

export default (opt: PrimaryConfigOptions): void => {
  const {editor} = opt;
  const styleManager = editor.StyleManager;

  styleManager.addType('pmw-columns-selector', {
    create(data: any): HTMLDivElement {
      const {change} = data;
      const props = data.props as ColumnsSelectorProps;
      props.component = props.component ?? editor.getSelected();
      
      const currComponent = props.component!;
      const isResponsiveMobile = currComponent.get(window.PMW.emails.props.IS_RESPONSIVE_MOBILE);
      const columnsSelector = document.createElement('div');
      const columnsLabelContainer = document.createElement('div');
      const selectedPresetID = parseInt(currComponent.get(props.property) ?? props.default ?? '0');

      columnsSelector.className = props.type;
      columnsLabelContainer.className = props.type;

      const [labelMarkup, selectorMarkup] = getColPresetsHTML(props, selectedPresetID, isResponsiveMobile);
      columnsSelector.innerHTML = selectorMarkup;
      columnsLabelContainer.innerHTML = labelMarkup;

      attachPresetHandler(props, change, columnsLabelContainer, columnsSelector);
      attachOpenMoreComponents(columnsLabelContainer, columnsSelector);

      return columnsLabelContainer;
    },

    emit(data: any, emitData: EmitData): void {
      const props = data.props as ColumnsSelectorProps;
      const {labelContainer, columnsSelector, presetID, toggleButton} = emitData;
      const isResponsiveMobile = props.component!.get(window.PMW.emails.props.IS_RESPONSIVE_MOBILE);
      
      // Tracking temporarily disabled on 8th Nov 2023
      // PMW.gtm.trackGA4CustomEvent('EmailMaker_StackLayout');

      if (toggleButton) {
        updateTogglesForResponsiveState(isResponsiveMobile, columnsSelector!);
      }

      if (presetID) {
        const [labelMarkup, selectorMarkup] = getColPresetsHTML(props, presetID, isResponsiveMobile);
        labelContainer!.innerHTML = labelMarkup;
        columnsSelector!.innerHTML = selectorMarkup;

        props.component!.set(props.property, presetID);

        editor.runCommand(window.PMW.emails.commands.UPDATE_COLUMNS_PRESET, {
          presetID: presetID,
        });
        attachPresetHandler(props, data.change, labelContainer!, columnsSelector!);
      }
    }
  });

  function updateTogglesForResponsiveState(isResponsiveMobile: boolean, columnsSelector: HTMLElement): void {
    if (!isResponsiveMobile) {
      $('.js-label-section-responsive-chk-mobile').prop('checked', false);
      columnsSelector.querySelector('.tgl-mobile')?.removeAttribute("checked");
    } else {
      $('.js-label-section-responsive-chk-mobile').prop('checked', true);
      columnsSelector.querySelector('.tgl-mobile')?.setAttribute("checked", "checked");
    }
  }

  function attachOpenMoreComponents(labelContainer: HTMLElement, columnsSelector: HTMLElement): void {
    window.PMW.onClick($(labelContainer), '.pmw-cta-button', function (): void {
      editor.runCommand(window.PMW.emails.commands.OPEN_SIDE_PANEL, {
        title: window.i18next.t('pmwjs_more_layouts'),
        content: columnsSelector,
      });
    });
  }

  function attachListenerPresetElementSelector(
    container: HTMLElement,
    change: (...args: any) => void,
    labelContainer: HTMLElement,
    columnsSelector: HTMLElement
  ): void {
    container.querySelectorAll('.js-comp-preset').forEach((presetEl: Element): void => {
      const presetID = parseInt(presetEl.getAttribute('data-preset-id') ?? '0');

      presetEl.addEventListener('click', function (): void {
        change({labelContainer, presetEl, presetID, columnsSelector});
      });
    });
  }

  function attachListenerPresetToggleButtonSelector(
    container: HTMLElement,
    props: ColumnsSelectorProps,
    change: (...args: any) => void,
    labelContainer: HTMLElement,
    columnsSelector: HTMLElement
  ): void {
    const responsiveTypeMobile = 'MOBILE';

    const toggleElement = container.querySelector('.tgl-mobile') as HTMLInputElement;
    toggleElement?.addEventListener('change', function (event: Event): void {
      const target = event.target as HTMLInputElement;
      editor.runCommand(window.PMW.emails.commands.UPDATE_SECTION_RESPONSIVE, {
        component: props.component,
        responsive: target.checked,
        type: responsiveTypeMobile
      });
      change({labelContainer, columnsSelector, toggleButton: event});
    });
  }

  function attachPresetHandler(
    props: ColumnsSelectorProps,
    change: (...args: any) => void,
    labelContainer: HTMLElement,
    columnsSelector: HTMLElement
  ): void {
    const attachToContainers = [labelContainer, columnsSelector];
    for (const container of attachToContainers) {
      attachListenerPresetElementSelector(container, change, labelContainer, columnsSelector);
      attachListenerPresetToggleButtonSelector(container, props, change, labelContainer, columnsSelector);
    }
  }

  function getStackOnMobileToggleHTML(isResponsiveMobile: boolean, customClasses = ''): string {
    return `<div class="toggle-container ${customClasses}">
                    <label class="toggle-text body-md-regular">${window.i18next.t('pmwjs_stack_layout_mobile')}</label>
                        <div class="toggle">
                            <input id="js-label-section-responsive-chk-mobile" class="tgl-mobile tgl js-label-section-responsive-chk-mobile" type="checkbox"  aria-label="Stack Layout" ${isResponsiveMobile ? 'checked' : ''}/>
                                <label for="js-label-section-responsive-chk-mobile" class="tgl-btn" aria-label="Stack layout"></label>
                        </div>
                </div>`;
  }

  function getColPresetsHTML(
    props: ColumnsSelectorProps,
    selectedPresetID: number,
    isResponsiveMobile = true
  ): [string, string] {

    function getLabelContainerPresets(selectedId: number): string {
      let selectedObject = props.list.find(o => o.id === selectedId);
      if (!selectedObject) {
        selectedObject = props.list.find(o => o.id === props.initalSelectedId);
      }
      let labelContainerObjects = selectedObject!.render(selectedPresetID);

      for (const id of props.previewIdFixedList) {
        const presetComp = props.list.find(o => o.id === id);
        labelContainerObjects += presetComp!.render(selectedPresetID);
      }
      return labelContainerObjects;
    }

    let labelContainerObjectsOutput: string;
    if (props.previewIdFixedList.includes(selectedPresetID)) {
      labelContainerObjectsOutput = getLabelContainerPresets(props.initalSelectedId);
    } else {
      labelContainerObjectsOutput = getLabelContainerPresets(selectedPresetID);
    }

    const labelContainer = `<div class="col-presets">` +
      labelContainerObjectsOutput
      + `</div>`
      + getStackOnMobileToggleHTML(isResponsiveMobile, 'spacing-m-b-4 spacing-m-t-4')
      + `<button class="pmw-cta-button -column-selector btn xsmall ghost -icon-right">
                            <i class="btn-icon icon-caret-right"></i>
                                <span class="btn-text js-cta-text">${window.i18next.t('pmwjs_view_all')}</span>
                    </button>`;

    const infoContainer = getStackOnMobileToggleHTML(isResponsiveMobile, 'spacing-m-t-4 spacing-m-b-4');
    const colPresets = `<div class="col-presets more-layout">`
      + props.list.map((preset: PresetComponentVO): string => {
        return preset.render(selectedPresetID);
      }).join('')
      + `</div>
                ${infoContainer}`;

    return [labelContainer, colPresets];
  }
};